import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Wrench, 
  CheckCircle,
  Shield,
  Calendar,
  BookOpen,
  Home,
  Trophy,
  Building2,
  Target,
  Settings,
  Hammer,
  Mountain,
  Cog
} from 'lucide-react';

const JesseSellers = () => {
  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Meet Jesse Sellers, Plumbing Technician at Colorado First Plumbing. 3 years plumbing experience, residential and commercial. Background in remodeling, carpentry, and construction. Colorado native with broad mechanical knowledge.' },
      { name: 'keywords', content: 'Jesse Sellers Colorado First Plumbing, plumbing technician, residential plumber Colorado, commercial plumbing, construction background, Colorado native plumber, Denver plumber' },
      { property: 'og:title', content: 'Jesse Sellers - Plumbing Technician | Colorado First Plumbing' },
      { property: 'og:description', content: 'Plumbing Technician with 3 years experience. Background in remodeling, carpentry, and construction. Colorado native with broad mechanical expertise.' },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/team/jesse-sellers' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });

    // Structured data for Jesse Sellers
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Jesse Sellers',
      'jobTitle': 'Plumbing Technician',
      'worksFor': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'url': 'https://colorado-plumbing.com'
      },
      'description': 'Plumbing Technician with 3 years of industry experience. Specializes in residential plumbing with commercial experience. Background in remodeling, carpentry, and construction.',
      'knowsAbout': ['Residential Plumbing', 'Commercial Plumbing', 'Remodeling', 'Carpentry', 'Construction', 'Mechanical Systems'],
      'birthPlace': 'Colorado',
      'url': 'https://colorado-plumbing.com/team/jesse-sellers'
    };

    let structuredDataScript = document.querySelector('#jesse-structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('id', 'jesse-structured-data');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      const script = document.querySelector('#jesse-structured-data');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-56 pb-16 bg-gradient-to-br from-trust-primary to-trust-deep text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-trust-primary/20 to-authority-navy/20"></div>
        <div className="container-custom relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Mountain className="w-8 h-8 text-white mr-3" />
                <span className="px-4 py-2 bg-white/20 rounded-full text-white font-medium text-sm border border-white/30">
                  Colorado Native
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Jesse Sellers
              </h1>
              <h2 className="text-2xl md:text-3xl text-white/90 font-semibold mb-6">
                Plumbing Technician
              </h2>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Three years of hands-on plumbing experience with a strong foundation in residential service and commercial work. 
                Jesse brings a unique combination of construction expertise and mechanical knowledge that makes him invaluable on any job site.
              </p>
              
              {/* Key Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">3+ Years</div>
                  <div className="text-sm text-white/80">Plumbing Experience</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">Multi-Trade</div>
                  <div className="text-sm text-white/80">Construction Background</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                
                <a 
                  href="#experience" 
                  className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30"
                >
                  <BookOpen size={16} className="mr-2" />
                  View Experience
                </a>
              </div>
            </div>
            
            {/* Team Photos */}
            <div className="flex justify-center">
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                <div className="aspect-square rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                  <img 
                    src="/colorado-plumbing-uploads/team/jesse-sellers.jpg" 
                    alt="Jesse Sellers - Plumbing Technician working on bathroom rough-in at Colorado First Plumbing" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                  <img 
                    src="/colorado-plumbing-uploads/team/jesse-sellers-team.jpg" 
                    alt="Jesse Sellers with Colorado First Plumbing team on job site" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Experience Section */}
      <section id="experience" className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Diverse Skills & Experience
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              Jesse's background spans multiple trades, bringing a comprehensive understanding of how homes and buildings work from the ground up.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Current Role */}
            <div className="bg-trust-primary/10 rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <Wrench className="w-8 h-8 text-trust-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-authority-navy">Plumbing Technician</h3>
                  <p className="text-trust-primary font-medium">Colorado First Plumbing and Sewer LLC</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">3+ years of professional plumbing experience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Primary focus on residential plumbing service</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Additional experience in commercial plumbing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Strong diagnostic and problem-solving skills</span>
                </li>
              </ul>
            </div>

            {/* Construction Background */}
            <div className="bg-success-primary/10 rounded-xl p-8 border border-success-primary/20">
              <div className="flex items-center mb-6">
                <Hammer className="w-8 h-8 text-success-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-authority-navy">Construction Background</h3>
                  <p className="text-success-primary font-medium">Multi-Trade Experience</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Remodeling and renovation expertise</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Skilled carpentry and finish work</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Fence installation and repair</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">General construction knowledge</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Expertise */}
      <section className="py-16 bg-neutral-light">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Broad Spectrum of Knowledge
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              Jesse's mechanical aptitude extends beyond plumbing, giving him a unique ability to understand and solve complex problems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Residential Plumbing */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-trust-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Home className="w-7 h-7 text-trust-primary" />
              </div>
              <h3 className="text-lg font-bold text-authority-navy mb-2">Residential Plumbing</h3>
              <p className="text-neutral-gray text-sm">
                Primary expertise in home plumbing systems, repairs, and installations.
              </p>
            </div>

            {/* Commercial Experience */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-success-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-success-primary" />
              </div>
              <h3 className="text-lg font-bold text-authority-navy mb-2">Commercial Work</h3>
              <p className="text-neutral-gray text-sm">
                Experience with larger commercial plumbing systems and projects.
              </p>
            </div>

            {/* Construction */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-action-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Hammer className="w-7 h-7 text-action-primary" />
              </div>
              <h3 className="text-lg font-bold text-authority-navy mb-2">Construction</h3>
              <p className="text-neutral-gray text-sm">
                Remodeling, carpentry, fencing, and general construction skills.
              </p>
            </div>

            {/* Mechanical */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-authority-navy/10 rounded-lg flex items-center justify-center mb-4">
                <Cog className="w-7 h-7 text-authority-navy" />
              </div>
              <h3 className="text-lg font-bold text-authority-navy mb-2">Mechanical</h3>
              <p className="text-neutral-gray text-sm">
                Vehicles, engines, and anything with mechanical systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Colorado Native Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-trust-primary to-trust-deep rounded-2xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <Mountain className="w-8 h-8 mr-3" />
                  <span className="text-xl font-bold">Colorado Native</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Born & Raised in Colorado
                </h2>
                <p className="text-white/90 text-lg mb-6">
                  As a Colorado native, Jesse understands the unique challenges that Colorado homes face — from the high altitude 
                  effects on plumbing systems to the freeze-thaw cycles that can wreak havoc on pipes. He knows the local building 
                  codes, the common issues in Denver-area homes, and what it takes to keep Colorado plumbing running smoothly year-round.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>Understands Colorado's unique plumbing challenges</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>Familiar with local building codes and standards</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>Knows the Denver metro area inside and out</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 text-center">
                  <Shield className="w-16 h-16 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Local Knowledge</h3>
                  <p className="text-white/80">
                    When you hire Jesse, you're getting someone who truly knows Colorado and its homes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Jesse */}
      <section className="py-16 bg-neutral-light">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Why Jesse Stands Out
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              A well-rounded technician who brings more to the table than just plumbing skills.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-trust-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-trust-primary" />
              </div>
              <h3 className="text-xl font-bold text-authority-navy mb-4">Problem Solver</h3>
              <p className="text-neutral-gray">
                With experience across multiple trades, Jesse approaches problems from different angles to find the best solution.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-success-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Settings className="w-8 h-8 text-success-primary" />
              </div>
              <h3 className="text-xl font-bold text-authority-navy mb-4">Versatile Skills</h3>
              <p className="text-neutral-gray">
                From plumbing to carpentry to mechanics, Jesse can handle a wide range of tasks and challenges.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-action-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-8 h-8 text-action-primary" />
              </div>
              <h3 className="text-xl font-bold text-authority-navy mb-4">Dedicated</h3>
              <p className="text-neutral-gray">
                Jesse takes pride in his work and is committed to getting the job done right the first time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-authority-navy to-trust-deep text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need a Skilled Plumber?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Jesse and the entire Colorado First Plumbing team are ready to help with your plumbing needs. 
            Call now for upfront pricing and expert service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            
            <a 
              href="/team" 
              className="inline-flex items-center px-8 py-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30 text-lg"
            >
              Meet the Full Team
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JesseSellers;
